import React from "react";

import AlertCircle from "@uui/AlertCircle";
import AlertTriangle from "@uui/AlertTriangle";
import ChevronRight from "@uui/ChevronRight";

import IconButton, { IconButtonProps } from "@mui/material/IconButton";
import styled from "@mui/material/styles/styled";

import { Row, StackProps } from "@rui/Stack";

type TagsContainerProps = StackProps & { showLabel?: boolean };

export const TagsContainer = styled<React.FC<TagsContainerProps>>(
  ({ showLabel: _s, ...props }) => <Row {...props} />
)(({ theme, showLabel = false }) => ({
  "& .add-tag": {
    borderRadius: theme.corners.xsmall,
    color: theme.palette.shades.neutral[300],
    padding: theme.spacing(0, 0.5),
    height: "18px",
    "& .add-tag__label": {
      transition: "all 100ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
      opacity: showLabel ? 1 : 0,
    },
    "&:hover": {
      backgroundColor: theme.palette.shades.ghost[500],
      color: theme.palette.shades.secondary[500],
      "& .add-tag__label": {
        color: theme.palette.shades.secondary[500],
        opacity: 1,
      },
    },
  },
}));

type ExpandButtonProps = IconButtonProps & { expanded: boolean };

export const ExpandButton = styled(
  ({ expanded: _, ...props }: ExpandButtonProps) => (
    <IconButton {...props}>
      <ChevronRight />
    </IconButton>
  )
)(({ expanded, theme }) => ({
  height: "32px",
  width: "32px",
  borderRadius: theme.corners.base,
  "& svg": {
    fontSize: "20px",
    color: expanded
      ? theme.palette.shades.primary[500]
      : theme.palette.shades.neutral[500],
    transform: expanded ? "rotate(90deg)" : "none",
    transition: "all 0.1s ease",
  },
}));

interface ReportAlertProps {
  severity?: "warning" | "error";
  title: string;
  description: string;
  action?: React.ReactNode;
}

export const ReportAlert = styled<React.FC<ReportAlertProps>>(
  ({ severity = "warning", title, description, action, ...props }) => {
    const Icon = severity === "error" ? AlertCircle : AlertTriangle;
    return (
      <Row spacing={0.5} {...props}>
        <Icon className="report-alert__icon" />
        <span className="report-alert__title">{title}:</span>
        <span className="report-alert__description">{description}</span>
        {action && <span className="report-alert__action">{action}</span>}
      </Row>
    );
  }
)(({ severity, theme }) => ({
  alignItems: "center",
  fontFamily: theme.typography.fontFamilySecondary,
  fontSize: "0.6875rem",
  fontWeight: 500,
  borderRadius: theme.corners.small,
  padding: "2px 4px",
  color: theme.palette.shades.neutral[600],
  backgroundColor:
    severity === "error"
      ? theme.palette.shades.negative[50]
      : theme.palette.shades.orange[50],
  "& .report-alert__icon": {
    fontSize: "inherit",
    color:
      severity === "error"
        ? theme.palette.error.main
        : theme.palette.warning.main,
  },
  "& .report-alert__title": {
    color:
      severity === "error"
        ? theme.palette.error.main
        : theme.palette.warning.main,
  },
  "& .report-alert__action": {
    marginLeft: theme.spacing(0.5),
  },
}));
